// Save store (rebuild.md §9.12): IndexedDB database `forge` (idb-keyval, keys `slot:N`, `slot:N.bak`,
// `meta`, `pack:<stageId>`). A .bak copy is written before each overwrite. If storage is unavailable
// the game plays from memory and the shell shows "Progress won't be saved on this device."
import { signal } from '@preact/signals';
import { createStore, del, get, set, type UseStore } from 'idb-keyval';
import { parseSave, newSave, type Save, type Settings } from './schema.ts';

export interface Meta {
  activeSlot: 1 | 2 | 3;
}

interface Backend {
  get<T>(k: string): Promise<T | undefined>;
  set(k: string, v: unknown): Promise<void>;
  del(k: string): Promise<void>;
}

const memory = new Map<string, unknown>();
const memoryBackend: Backend = {
  get: async <T,>(k: string) => memory.get(k) as T | undefined,
  set: async (k, v) => void memory.set(k, structuredClone(v)),
  del: async (k) => void memory.delete(k),
};

/** True when progress is only kept in memory. */
export const storageUnavailable = signal(false);

let backend: Backend = memoryBackend;

export async function openStore(): Promise<Backend> {
  try {
    if (typeof indexedDB === 'undefined') throw new Error('no IndexedDB');
    const store: UseStore = createStore('forge', 'kv');
    await set('probe', 1, store);
    backend = {
      get: <T,>(k: string) => get<T>(k, store),
      set: (k, v) => set(k, v, store),
      del: (k) => del(k, store),
    };
    storageUnavailable.value = false;
  } catch {
    backend = memoryBackend;
    storageUnavailable.value = true;
  }
  return backend;
}

const nowIso = () => new Date().toISOString();

export async function readMeta(): Promise<Meta> {
  return (await backend.get<Meta>('meta')) ?? { activeSlot: 1 };
}

export async function writeMeta(m: Meta): Promise<void> {
  await backend.set('meta', m);
}

/** Load a slot, falling back to its .bak copy if the main copy fails validation; null if empty. */
export async function loadSlot(slot: 1 | 2 | 3): Promise<Save | null> {
  for (const key of [`slot:${slot}`, `slot:${slot}.bak`]) {
    const raw = await backend.get<unknown>(key);
    if (raw === undefined) continue;
    try {
      return parseSave(raw);
    } catch {
      /* try the backup */
    }
  }
  return null;
}

export async function loadOrCreate(slot: 1 | 2 | 3): Promise<Save> {
  return (await loadSlot(slot)) ?? newSave(slot, nowIso());
}

export async function saveSlot(save: Save): Promise<void> {
  const key = `slot:${save.slot}`;
  const prev = await backend.get<unknown>(key);
  if (prev !== undefined) await backend.set(`${key}.bak`, prev);
  await backend.set(key, { ...save, updatedAt: nowIso() });
}

export async function deleteSlot(slot: 1 | 2 | 3): Promise<void> {
  await backend.del(`slot:${slot}`);
  await backend.del(`slot:${slot}.bak`);
}

export async function slotSummaries(): Promise<{ slot: 1 | 2 | 3; save: Save | null }[]> {
  return Promise.all(([1, 2, 3] as const).map(async (slot) => ({ slot, save: await loadSlot(slot) })));
}

// ---------------------------------------------------------------- export / import

async function sha256(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface ExportFile {
  kind: 'forge-and-co-save';
  version: 1;
  exportedAt: string;
  checksum: string;
  save: Save;
}

/** Serialise a slot for "move devices without an account". */
export async function exportSave(save: Save): Promise<string> {
  const body = JSON.stringify(save);
  const file: ExportFile = { kind: 'forge-and-co-save', version: 1, exportedAt: nowIso(), checksum: await sha256(body), save };
  return JSON.stringify(file, null, 2);
}

/** Parse, verify the checksum and validate an exported file; returns the save (re-slotted). */
export async function importSave(text: string, intoSlot: 1 | 2 | 3): Promise<Save> {
  let file: ExportFile;
  try {
    file = JSON.parse(text) as ExportFile;
  } catch {
    throw new Error('That file is not a Forge & Co. save.');
  }
  if (file.kind !== 'forge-and-co-save' || !file.save) throw new Error('That file is not a Forge & Co. save.');
  if ((await sha256(JSON.stringify(file.save))) !== file.checksum) throw new Error('The save file is damaged (checksum mismatch).');
  const save = parseSave(file.save);
  return { ...save, slot: intoSlot };
}

export type { Save, Settings };

// ---------------------------------------------------------------- generic records (packs)

export const kvGet = <T,>(key: string): Promise<T | undefined> => backend.get<T>(key);
export const kvSet = (key: string, value: unknown): Promise<void> => backend.set(key, value);
export const kvDel = (key: string): Promise<void> => backend.del(key);
